import { __rest } from "tslib";
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "../../lib/utils";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Button,
 *   Card,
 *   CardContent,
 *   CardDescription,
 *   CardFooter,
 *   CardHeader,
 *   CardTitle,
 *   Input,
 *   Label,
 *   Tabs,
 *   TabsContent,
 *   TabsList,
 *   TabsTrigger,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <Tabs defaultValue="account" className="w-[400px]">
 *       <TabsList className="grid w-full grid-cols-2">
 *         <TabsTrigger value="account">Account</TabsTrigger>
 *         <TabsTrigger value="password">Password</TabsTrigger>
 *       </TabsList>
 *       <TabsContent value="account">
 *         <Card>
 *           <CardHeader>
 *             <CardTitle>Account</CardTitle>
 *             <CardDescription>
 *               Make changes to your account here. Click save when you're done.
 *             </CardDescription>
 *           </CardHeader>
 *           <CardContent className="space-y-2">
 *             <div className="space-y-1">
 *               <Label htmlFor="name">Name</Label>
 *               <Input id="name" defaultValue="Pedro Duarte" />
 *             </div>
 *             <div className="space-y-1">
 *               <Label htmlFor="username">Username</Label>
 *               <Input id="username" defaultValue="@peduarte" />
 *             </div>
 *           </CardContent>
 *           <CardFooter>
 *             <Button>Save changes</Button>
 *           </CardFooter>
 *         </Card>
 *       </TabsContent>
 *       <TabsContent value="password">
 *         <Card>
 *           <CardHeader>
 *             <CardTitle>Password</CardTitle>
 *             <CardDescription>
 *               Change your password here. After saving, you'll be logged out.
 *             </CardDescription>
 *           </CardHeader>
 *           <CardContent className="space-y-2">
 *             <div className="space-y-1">
 *               <Label htmlFor="current">Current password</Label>
 *               <Input id="current" type="password" />
 *             </div>
 *             <div className="space-y-1">
 *               <Label htmlFor="new">New password</Label>
 *               <Input id="new" type="password" />
 *             </div>
 *           </CardContent>
 *           <CardFooter>
 *             <Button>Save password</Button>
 *           </CardFooter>
 *         </Card>
 *       </TabsContent>
 *     </Tabs>
 *   )
 * }
 * ```
 */
const Tabs = TabsPrimitive.Root;
const TabsList = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(TabsPrimitive.List, Object.assign({ ref: ref, className: cn("inline-flex items-center justify-center rounded-md bg-input p-1 text-muted-foreground", className) }, props)));
});
TabsList.displayName = TabsPrimitive.List.displayName;
const TabsTrigger = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(TabsPrimitive.Trigger, Object.assign({ ref: ref, className: cn("inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm", className) }, props)));
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
const TabsContent = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(TabsPrimitive.Content, Object.assign({ ref: ref, className: cn("mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", className) }, props)));
});
TabsContent.displayName = TabsPrimitive.Content.displayName;
export { Tabs, TabsList, TabsTrigger, TabsContent };
//# sourceMappingURL=tabs.js.map